import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  allRulesSatisfied,
  evaluatePasswordRules,
  type PasswordRules,
} from '@wineryxplorer/shared';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PasswordRequirements } from '../components/auth/PasswordRequirements';
import { LoginAccountPrompt } from '../components/auth/LoginAccountPrompt';
import { LoginMessages } from '../components/auth/LoginMessages';
import { LoginOAuthPanel } from '../components/auth/LoginOAuthPanel';
import { LoginTabs } from '../components/auth/LoginTabs';
import { SignupIdentityFields } from '../components/auth/SignupIdentityFields';
import { SignupAgeFields } from '../components/auth/SignupAgeFields';
import {
  birthdayToDate,
  getBirthdayError,
  getEmailError,
  getNameError,
  getPasswordError,
  getUserNameError,
} from '../components/auth/loginValidation';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { useOAuthAgeDeclaration } from '../hooks/useOAuthAgeDeclaration';
import type { LoginTab } from '../components/auth/LoginTabs';

type TabType = LoginTab;

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetchUser } = useAuth();
  // `?signup=...` opens the signup tab directly, so callers that send someone
  // here to make an account (the /itineraries CTA, the venue-admin prompt on
  // Experiences) land on the right form instead of the sign-in one.
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    searchParams.get('signup') !== null ? 'signup' : 'signin'
  );

  // Email/Password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userName, setUserName] = useState('');
  // Date of birth and the country it is judged against. Required to create an
  // account — the age check has to happen before the account exists, not in
  // onboarding afterwards. Defaulting the country would quietly pick a legal
  // age on someone's behalf, so it starts empty.
  const [birthday, setBirthday] = useState('');
  const [country, setCountry] = useState('');
  const [birthdayError, setBirthdayError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [nameError, setNameError] = useState('');
  const [userNameError, setUserNameError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Create Account keeps the email fields behind a button, so Google stays in view.
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const emailFormVisible = activeTab === 'signin' || showEmailSignup;
  const [passwordRules, setPasswordRules] = useState<PasswordRules>(() =>
    evaluatePasswordRules('')
  );

  const { showOAuthAge, setShowOAuthAge, handleOAuthSignin } = useOAuthAgeDeclaration({
    activeTab,
    birthday,
    country,
    onBirthdayError: setBirthdayError,
    onFormError: setLoginError,
  });

  // Real-time validation helpers — rules live in ../components/auth/loginValidation
  const validateEmail = (emailValue: string) => setEmailError(getEmailError(emailValue));
  const validateName = (nameValue: string) => setNameError(getNameError(nameValue));
  const validateUserName = (userNameValue: string) =>
    setUserNameError(getUserNameError(userNameValue));
  const validateBirthday = (birthdayValue: string, countryValue: string) =>
    setBirthdayError(getBirthdayError(birthdayValue, countryValue));

  // tRPC mutations
  const registerMutation = trpc.auth.register.useMutation();
  const loginMutation = trpc.auth.login.useMutation();

  const isLoading = registerMutation.isPending || loginMutation.isPending;

  // Password validation function
  const validatePassword = (pwd: string) => {
    const rules = evaluatePasswordRules(pwd);
    setPasswordRules(rules);
    setPasswordError(getPasswordError(pwd, rules));
  };

  // Email/Password handlers
  const handleEmailSignup = async () => {
    // Basic client-side checks
    if (!email || !name || !userName || !password || !birthday || !country) {
      setLoginError('Please fill in all fields');
      return;
    }

    // The server refuses an underage registration outright; checking here too
    // means someone finds out before their password is sent anywhere.
    const ageError = getBirthdayError(birthday, country);
    if (ageError) {
      setBirthdayError(ageError);
      setLoginError(ageError);
      // Failing the gate is a stop, so the rejected date does not stay sitting
      // in the field waiting to be nudged by a year.
      setBirthday('');
      return;
    }

    setLoginError('');
    setSuccessMessage('');

    try {
      const result = await registerMutation.mutateAsync({
        email,
        password,
        name,
        userName,
        birthday: birthdayToDate(birthday).toISOString(),
        country,
      });

      // Store session token and refetch user
      localStorage.setItem('session-token', result.sessionToken);
      await refetchUser();

      // User is now logged in, navigate to onboarding
      navigate('/onboarding');
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Failed to create account';
      setLoginError(errorMessage);
    }
  };

  const handleEmailSignin = async () => {
    // Basic client-side checks
    if (!email || !password) {
      setLoginError('Please fill in all fields');
      return;
    }

    setLoginError('');
    setSuccessMessage('');

    try {
      const result = await loginMutation.mutateAsync({
        email,
        password,
      });

      // Store session token and refetch user
      localStorage.setItem('session-token', result.sessionToken);
      await refetchUser();

      if (!result.user.onboardingComplete) {
        navigate('/onboarding');
      } else {
        navigate('/');
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Failed to sign in';
      setLoginError(errorMessage);
    }
  };

  const handleGoogleSignin = () => {
    handleOAuthSignin('google');
  };

  // TODO: Apple's form_post callback is a cross-site POST, so the SameSite=Lax
  // age-declaration cookie is not sent; change that before enabling Apple on web,
  // and add 'apple' to WEB_OAUTH_PROVIDERS in apps/api/src/auth/fastify-plugin.ts.
  const _handleAppleSignin = () => {
    handleOAuthSignin('apple');
  };

  // Switching tabs clears every field-level and form-level message.
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'signup') {
      // The sign-up form shows the age fields itself.
      setShowOAuthAge(false);
      setShowEmailSignup(false);
    } else if (activeTab === 'signup') {
      // Stale sign-up values must not follow someone to the Google button.
      setBirthday('');
      setCountry('');
    }
    setEmailError('');
    setPasswordError('');
    setNameError('');
    setUserNameError('');
    setBirthdayError('');
    setLoginError('');
    setSuccessMessage('');
  };

  const ageFields = (
    <SignupAgeFields
      birthday={birthday}
      birthdayError={birthdayError}
      onBirthdayChange={(v) => {
        setBirthday(v);
        validateBirthday(v, country);
      }}
      country={country}
      onCountryChange={(v) => {
        setCountry(v);
        validateBirthday(birthday, v);
      }}
      disabled={isLoading}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 pt-6 pb-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to WineryXplorer</h1>
          <p className="text-gray-600">
            {activeTab === 'signin'
              ? 'Sign in to your account'
              : 'Create your account to get started'}
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          {/* Tabs */}
          <LoginTabs activeTab={activeTab} onSelectTab={handleTabChange} />

          <LoginMessages error={loginError} success={successMessage} />

          {/* Every way of creating an account needs the age, so on Create
              Account it comes before the choice of Google or email. */}
          {(activeTab === 'signup' || showOAuthAge) && (
            <div className="space-y-4 mb-6">{ageFields}</div>
          )}
          <LoginOAuthPanel
            onGoogleSignin={handleGoogleSignin}
            disabled={isLoading}
            dividerLabel={activeTab === 'signup' ? 'or' : 'Or continue with email'}
          />

          {!emailFormVisible ? (
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={() => setShowEmailSignup(true)}
              disabled={isLoading}
            >
              Sign up with email
            </Button>
          ) : (
            <div className="space-y-4">
              {activeTab === 'signup' && (
                <SignupIdentityFields
                  name={name}
                  nameError={nameError}
                  onNameChange={(newName) => {
                    setName(newName);
                    validateName(newName);
                  }}
                  userName={userName}
                  userNameError={userNameError}
                  onUserNameChange={(newUserName) => {
                    setUserName(newUserName);
                    validateUserName(newUserName);
                  }}
                  disabled={isLoading}
                />
              )}

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  setLoginError('');
                  setSuccessMessage('');
                  if (activeTab === 'signup') {
                    validateEmail(newEmail);
                  } else {
                    setEmailError('');
                  }
                }}
                placeholder="you@example.com"
                error={emailError}
                success={activeTab === 'signup' && email.length > 0 && !emailError}
                fullWidth
                required
                disabled={isLoading}
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => {
                  const newPassword = e.target.value;
                  setPassword(newPassword);
                  setLoginError('');
                  setSuccessMessage('');
                  if (activeTab === 'signup') {
                    validatePassword(newPassword);
                  } else {
                    setPasswordError('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (activeTab === 'signin') {
                      handleEmailSignin();
                    } else {
                      handleEmailSignup();
                    }
                  }
                }}
                placeholder={activeTab === 'signup' ? 'Create a strong password' : 'Your password'}
                error={passwordError}
                success={activeTab === 'signup' && allRulesSatisfied(passwordRules)}
                fullWidth
                required
                disabled={isLoading}
              />

              {activeTab === 'signup' && (
                <div className="mt-2">
                  <PasswordRequirements password={password} rules={passwordRules} />
                </div>
              )}

              {activeTab === 'signin' && (
                <div className="text-right">
                  <button
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={activeTab === 'signin' ? handleEmailSignin : handleEmailSignup}
                loading={isLoading}
                disabled={
                  isLoading ||
                  (activeTab === 'signup' &&
                    (!name.trim() ||
                      !userName ||
                      !email ||
                      !password ||
                      !birthday ||
                      !country ||
                      !!nameError ||
                      !!userNameError ||
                      !!emailError ||
                      !!passwordError ||
                      !!birthdayError ||
                      !allRulesSatisfied(passwordRules))) ||
                  (activeTab === 'signin' && (!email || !password))
                }
              >
                {activeTab === 'signin' ? 'Sign In' : 'Create Account'}
              </Button>
            </div>
          )}
        </Card>

        {/* Footer */}
        <LoginAccountPrompt activeTab={activeTab} onSelectTab={handleTabChange} />
      </div>
    </div>
  );
};
